/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, inject, signal} from '@angular/core';
import * as _ from 'lodash';
import template from './report-template-edit-page.component.html';
import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {AngularModalService} from '../common/modal/angular-modal.service';
import {ADN_GRIDSTER_CONFIG} from '../config/gridster-config';
import {TOOLBAR_BUTTONS} from '../common/directives/notes/notes-constants';
import {ADN_REPORT_COMPONENTS, ADN_REPORT_TYPES} from '../report/report-types';
import type {ReportTemplateGridsterResource} from './report-template-gridster-item.component';
import {ReportTemplateWidgetModalComponent} from './report-template-widget-modal.component';
import {OgTranslateService} from '../common/filters/og-translate.service';
import {TeamApiService} from '../admin/team/team-api.service';
import {
  applyLoadedReportTranslationDefaults,
  buildReportDateFormats,
  createEmptyReportTypeComponents,
  getAvailableReportLanguages,
  getReportTemplateTeamQueryParams,
  getReportTemplateTypes,
  initReportTemplateComponent,
  initReportTemplateModel,
  normalizeReportTemplateComponent,
  prepareReportTemplateForSubmit,
  reportTypeSupportsLabelFilter,
  reportTypeSupportsPoliticalFilter,
  restoreReportTemplateComponentsAfterReload,
  timeChangeReportComponent
} from './report-template-commons';
import {ReportTranslationApiService} from '../report-translation/report-translation-api.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {ReportTemplateApiService, ReportTemplateModel} from './report-template-api.service';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';
import {ReportTemplateEditWorkspaceComponent} from './report-template-edit-workspace.component';

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED, ReportTemplateEditWorkspaceComponent],
  selector: 'adn-report-template-edit-page',
  template
})
export class ReportTemplateEditPageComponent extends SignalAdminResourceEditPageBase<ReportTemplateModel> {
  readonly widgets = ADN_REPORT_COMPONENTS;
  readonly toolbarButtons = TOOLBAR_BUTTONS;
  readonly dateFormats = buildReportDateFormats();

  showTeams = false;
  isAxAdvertiserAdmin = false;
  isAxAdvertiser = false;
  logoSpecify = false;
  showHeaderContent = false;
  showFooterContent = false;
  showTranslations = false;
  isPoliticalAdsPlatform = false;
  labelFilterMode: 'includeLabels' | 'excludeLabels' = 'includeLabels';
  labelFilter = {labels: [] as string[]};

  private readonly teamsState = signal<any[]>([]);
  private readonly reportTranslationsState = signal<any[]>([]);
  private readonly translationsState = signal<Record<string, Record<string, string>>>({});
  private readonly translationFieldsState = signal<Record<string, string>>({});
  get teams(): any[] { return this.teamsState(); }
  set teams(value: any[]) { this.teamsState.set(value); }
  get reportTranslations(): any[] { return this.reportTranslationsState(); }
  set reportTranslations(value: any[]) { this.reportTranslationsState.set(value); }
  get translations(): Record<string, Record<string, string>> { return this.translationsState(); }
  set translations(value: Record<string, Record<string, string>>) { this.translationsState.set(value); }
  get translationFields(): Record<string, string> { return this.translationFieldsState(); }
  set translationFields(value: Record<string, string>) { this.translationFieldsState.set(value); }
  reportTypes: string[] = [];
  availableLangs: Record<string, string> = {};

  reportTypeComponents = createEmptyReportTypeComponents();
  gridsterOpts = _.merge({}, ADN_GRIDSTER_CONFIG.RESOURCES, {
    draggable: {
      stop: () => this.markExternalFormDirty()
    }
  });

  readonly emptyComponents: any[] = [];
  readonly gridsterResource: ReportTemplateGridsterResource = {
    model: null,
    widgets: this.widgets,
    toolbarButtons: this.toolbarButtons,
    dateFormats: this.dateFormats,
    isAxAdvertiser: false,
    removeComponent: (component) => this.removeComponent(component),
    timeChange: (component) => this.timeChange(component),
    markDirty: () => this.markExternalFormDirty()
  };

  private lastComponentId = 0;
  private savedModel: any;
  private initialized = false;
  private hasExistingLabelFilter = false;

  constructor(
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly teamApi: TeamApiService,
    private readonly reportTranslationApi: ReportTranslationApiService,
    private readonly ogTranslate: OgTranslateService,
    private readonly angularModalService: AngularModalService
  ) {
    super(inject(ReportTemplateApiService), 'reportTemplate');
  }

  getCurrentComponents(): any[] {
    const reportType = this.editingModel?.reportType;
    if (!reportType) {
      return this.emptyComponents;
    }
    return this.reportTypeComponents[reportType]?.components || this.emptyComponents;
  }

  get showPoliticalFilter(): boolean {
    return this.isPoliticalAdsPlatform && reportTypeSupportsPoliticalFilter(this.editingModel?.reportType);
  }

  get showLabelFilter(): boolean {
    const reportType = this.editingModel?.reportType;
    return reportTypeSupportsLabelFilter(reportType)
      || (this.hasExistingLabelFilter && !!ADN_REPORT_TYPES[reportType] && reportType !== 'NETWORK');
  }

  compareTeams(a: any, b: any): boolean {
    return a?.id === b?.id;
  }

  compareReportTranslations(a: any, b: any): boolean {
    return a?.id === b?.id;
  }

  onReportTypeChange(): void {
    this.syncPoliticalFilter();
    this.syncLabelFilter();
    this.markExternalFormDirty();
  }

  onLabelFilterChange(): void {
    this.syncLabelFilter();
    this.markExternalFormDirty();
  }

  updateTranslations(langKey: string): void {
    if (!this.translations.en) {
      return;
    }
    _.forEach(this.translations.en, (val, key) => {
      this.editingModel.parameters[key] = (this.translations[langKey] || this.translations.en)[key];
    });
    this.syncTranslationFields();
    this.markExternalFormDirty();
  }

  protected override afterEditingModelReset(): void {
    this.gridsterResource.model = this.editingModel;
    this.resetLabelFilter();
    if (this.initialized) {
      // Undo/save rebuild editingModel; keep the logo toggle in step or the next save clears the logo.
      this.logoSpecify = !!this.editingModel.logo;
      this.syncPoliticalFilter();
      this.reloadReportTypeComponentsFromModel();
      this.syncTranslationFields();
      return;
    }
    this.initialized = true;
    const perms = this.LocalUserPermissions.getAllVisPermissions();
    this.isAxAdvertiserAdmin = perms.AX_ADVERTISER_ADMIN === true || perms.AX_ADVERTISER_TEAM_ADMIN === true;
    this.isAxAdvertiser = perms.AX_ADVERTISER === true;
    this.isPoliticalAdsPlatform = this.LocalNetworkProfile.isPoliticalAdsPlatform();
    this.gridsterResource.isAxAdvertiser = this.isAxAdvertiser;
    this.showTeams = this.LocalNetworkProfile.isPureMarketplacePlatform() || this.LocalNetworkProfile.isSuiPlatform();
    this.reportTypes = getReportTemplateTypes(this.LocalNetworkProfile.isSuiPlatform());
    this.availableLangs = getAvailableReportLanguages(this.LocalNetworkProfile.getNetworkId());

    this.savedModel = _.cloneDeep(this.model);
    this.reportTypeComponents = createEmptyReportTypeComponents();
    this.logoSpecify = !!this.editingModel.logo;
    initReportTemplateModel(
      this.editingModel,
      this.savedModel,
      this.reportTypeComponents,
      () => ++this.lastComponentId
    );
    this.syncPoliticalFilter();
    this.resetComponentIds();

    this.loadTeams(perms);
    this.loadReportTranslations();
    this.loadTranslationDefaults();
    this.syncTranslationFields();
  }

  protected override afterSave(model: ReportTemplateModel): void {
    this.savedModel = _.cloneDeep(model);
    if (!this.savedModel.components?.length && this.editingModel.components?.length) {
      this.savedModel.components = _.cloneDeep(this.editingModel.components);
    }
  }

  protected override prepareForSubmit(model: ReportTemplateModel): void {
    this.syncPoliticalFilter();
    prepareReportTemplateForSubmit(
      model,
      this.reportTypeComponents,
      this.logoSpecify,
      (key: string, defaultValue?: string) => this.ogTranslate.transform(key, defaultValue)
    );
    this.syncLabelFilter();
  }

  openAddComponents(): void {
    const reportType = ADN_REPORT_TYPES[this.editingModel.reportType];
    if (!reportType) {
      return;
    }
    const components = _.filter(reportType.components, (component) => {
      return this.editingModel.reportFormat !== 'CSV' || component.csv;
    });

    const modalRef = this.angularModalService.show(ReportTemplateWidgetModalComponent, {
      class: 'modal-lg adnDialog',
      initialState: {components}
    });
    (modalRef.content as ReportTemplateWidgetModalComponent).onSaved.subscribe((selections: Record<string, any>) => {
      if (!selections) {
        return;
      }
      _.forEach(selections, (component) => {
        const pushedComponent = {
          reportComponent: {id: component.id},
          parameters: _.cloneDeep(component.parameters),
          uniqueId: ++this.lastComponentId
        };
        initReportTemplateComponent(pushedComponent);
        this.reportTypeComponents[this.editingModel.reportType].components.push(pushedComponent);
      });
      this.markExternalFormDirty();
    });
  }

  private loadTeams(perms: any): void {
    this.teamApi.listOptions(getReportTemplateTeamQueryParams(perms)).subscribe((page) => {
      this.teams = page.results || [];
    });
  }

  private loadReportTranslations(): void {
    this.reportTranslationApi.list({
      includeHidden: false,
      includeActive: true,
      includeInactive: false,
      orderBy: 'name',
      orderByDirection: 'ASCENDING',
      page: 1,
      pageSize: 1001,
      concise: false
    }).subscribe((data) => {
      this.reportTranslations = data.results || [];
    });
  }

  private loadTranslationDefaults(): void {
    if (this.translations.en) {
      return;
    }
    this.reportTranslationApi.getDefaults().subscribe((transDefaults) => {
      this.translations = transDefaults || {en: {}};
      applyLoadedReportTranslationDefaults(
        this.editingModel,
        this.translations,
        (key: string, defaultValue?: string) => this.ogTranslate.transform(key, defaultValue)
      );
      this.syncTranslationFields();
    });
  }

  private syncTranslationFields(): void {
    this.translationFields = this.translations.en ? {...this.translations.en} : {};
  }

  private syncPoliticalFilter(): void {
    this.editingModel.parameters = this.editingModel.parameters || {};
    if (this.showPoliticalFilter) {
      this.editingModel.parameters.political = this.editingModel.parameters.political || 'ALL';
    } else {
      delete this.editingModel.parameters.political;
    }
  }

  private resetLabelFilter(): void {
    const parameters = this.editingModel.parameters || {};
    const toLabels = (value: string[] | string): string[] => {
      return (Array.isArray(value) ? value : [value]).filter((label) => typeof label === 'string' && label.trim().length > 0);
    };
    const included = toLabels(parameters.includeLabels);
    const excluded = toLabels(parameters.excludeLabels);
    this.hasExistingLabelFilter = included.length > 0 || excluded.length > 0;
    this.labelFilterMode = included.length || !excluded.length ? 'includeLabels' : 'excludeLabels';
    this.labelFilter = {labels: included.length ? included : excluded};
  }

  private syncLabelFilter(): void {
    const parameters = this.editingModel.parameters = this.editingModel.parameters || {};
    delete parameters.includeLabels;
    delete parameters.excludeLabels;
    if (!this.showLabelFilter) {
      this.labelFilter.labels = [];
      this.hasExistingLabelFilter = false;
    } else if (this.labelFilter.labels.length) {
      parameters[this.labelFilterMode] = this.labelFilter.labels.slice();
    }
  }

  private reloadReportTypeComponentsFromModel(): void {
    const reportType = this.editingModel?.reportType;
    if (!reportType) {
      return;
    }
    const components = this.editingModel.components || this.savedModel?.components;
    if (!components?.length) {
      return;
    }
    const previousComponents = this.reportTypeComponents[reportType].components || [];
    this.reportTypeComponents[reportType].components = restoreReportTemplateComponentsAfterReload(
      _.cloneDeep(components),
      previousComponents,
      () => ++this.lastComponentId
    );
    _.forEach(this.reportTypeComponents[reportType].components, (component) => {
      component.parameters = component.parameters || {};
      if (typeof component.reportComponent === 'string') {
        component.reportComponent = {id: component.reportComponent};
      }
      if (!_.isFinite(component.uniqueId)) {
        component.uniqueId = ++this.lastComponentId;
      }
      normalizeReportTemplateComponent(component);
    });
    this.resetComponentIds();
  }

  private removeComponent(component: any): void {
    _.pull(this.reportTypeComponents[this.editingModel.reportType].components, component);
    this.markExternalFormDirty();
  }

  private timeChange(component: any): void {
    timeChangeReportComponent(component, this.dateFormats);
    this.markExternalFormDirty();
  }

  private resetComponentIds(): void {
    this.lastComponentId = 0;
    _.forEach(this.reportTypeComponents, (bucket) => {
      _.forEach(bucket.components, (component) => {
        if (_.isFinite(component.uniqueId) && component.uniqueId > this.lastComponentId) {
          this.lastComponentId = component.uniqueId;
        }
      });
    });
  }
}
